import { Button, ConfigProvider, Dropdown, FormInstance, Tooltip } from 'antd';
import React from 'react';
import { CURRENT_INPUT_TAG, CURRENT_USER_INPUT_TAG, INPUT_TAG, USER_INPUT_TAG } from '../Messages/patterns';
import classes from './CustomInput.module.scss';
import colors from 'scss/variables/colors.module.scss';
import RowLoader from 'components/Sceletons/RowLoader';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { ItemType } from 'antd/es/menu/interface';
import { setIsOpenDocsMenu, setIsOpenUsersMenu, setMessageContent } from 'store/slices/chatsSlice';
import cn from 'classnames';
import { SendOutlined } from '@ant-design/icons';
import { setCursorToTheEnd } from 'helpers/setCursorToTheEnd';
import ModeDropdown from './Elements/ModeDropdown';

const message_length = window.REACT_APP_MESSAGE_LENGTH !== 'REPLACE_REACT_APP_MESSAGE_LENGTH' ? window.REACT_APP_MESSAGE_LENGTH : process.env.REACT_APP_MESSAGE_LENGTH; 
const MAX_LENGTH = Number(message_length) || 1000;

type CustomInputProps = {
  onFinish: () => Promise<void>;
  onOpenAttachment: () => void;
  setAttachment: React.Dispatch<React.SetStateAction<File[]>>;
  tagsDocuments: ItemType[] | undefined;
  form: FormInstance<any>;
  editableRef: React.RefObject<HTMLDivElement>;
  needToShowMenus: boolean;
  parent?: string;
  aiAssistant?: boolean;
  isSendingDisabled?: boolean;
};

export default function CustomInput({
  onFinish,
  onOpenAttachment,
  setAttachment,
  tagsDocuments,
  form,
  editableRef,
  needToShowMenus,
  parent,
  aiAssistant,
  isSendingDisabled
}: CustomInputProps) {
  const [userSearch, setUserSearch] = React.useState<string>('');
  const [search, setSearch] = React.useState<string>('');
  const [isOpenHelp, setIsOpenHelp] = React.useState(false);

  const { isLoadingTagsDocuments, isOpenDocsMenu, isOpenUsersMenu, messageForReply, messageContent } = useAppSelector((state) => state.chat);
  const { users } = useAppSelector((state) => state.dataUsers);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const onUserDropdownClick = (info: any) => {
    const [email, ...nameParts] = info.key.split(' ');
    const name = nameParts.join(' ');
    
    const idx = editableRef.current?.innerHTML.indexOf(CURRENT_USER_INPUT_TAG.OPEN)!;
    const holderTemplate = editableRef.current?.innerHTML.slice(idx);
    const closeIdx = holderTemplate?.indexOf(CURRENT_USER_INPUT_TAG.CLOSE);

    const beforeTemplate = editableRef.current?.innerHTML.slice(0, idx - 1);
    const afterTemplate = holderTemplate?.slice(closeIdx! + CURRENT_USER_INPUT_TAG.CLOSE.length);
    editableRef.current?.focus();
    editableRef.current!.innerHTML = `${beforeTemplate} ${USER_INPUT_TAG.OPEN.replace('>', ` data-email="${email}">`)}@${name}${USER_INPUT_TAG.CLOSE}${afterTemplate?.trim()}&nbsp;`;

    document.execCommand('selectAll', false, undefined);
    document.getSelection()?.collapseToEnd();

    dispatch(setIsOpenUsersMenu(false));
  };  

  const onDropDownCLick = (info: any) => {
    let arrOfSplitesKey = info.key.split(' ');
    arrOfSplitesKey.shift();
    
    const label = arrOfSplitesKey.join(' ');
    const idx = editableRef.current?.innerHTML.indexOf(CURRENT_INPUT_TAG.OPEN)!;
    const holderTemplate = editableRef.current?.innerHTML.slice(idx);
    const closeIdx = holderTemplate?.indexOf(CURRENT_INPUT_TAG.CLOSE);

    const beforeTemplate = editableRef.current?.innerHTML.slice(0, idx - 1);
    const afterTemplate = holderTemplate?.slice(closeIdx);
    editableRef.current?.focus();
    editableRef.current!.innerHTML = `${beforeTemplate} ${INPUT_TAG.OPEN}#${label}${INPUT_TAG.CLOSE}${afterTemplate?.trim()}&nbsp;`;

    // помещаем курсор в конце строки
    document.execCommand('selectAll', false, undefined);
    document.getSelection()?.collapseToEnd();

    dispatch(setIsOpenDocsMenu(false));
  };

  const filteredTags = React.useMemo(
    () => tagsDocuments?.map((tag: ItemType<any>) => ({ ...tag, onClick: onDropDownCLick }))
      .filter((tag) => (tag?.key! as string).includes(search)),
    [search, tagsDocuments]
  );

  const filteredUsers = React.useMemo(() => {
    const usersList = users?.filter((user) => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email.toLowerCase();
      const searchLower = userSearch.toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    }) || [];

    return usersList.map((user) => ({
      key: `${user.email} ${user.first_name} ${user.last_name}`,
      label: `${user.first_name} ${user.last_name} (${user.email})`,
      onClick: onUserDropdownClick,
    }));
  }, [userSearch, users]);

  React.useEffect(() => {
    const element = editableRef.current;
    if (!element) return;

    if (messageContent) {
      element.innerHTML = messageContent;
      setCursorToTheEnd(element)
    }
    // Создаем наблюдатель за изменениями
    const observer = new MutationObserver(() => {
      const content = element.innerHTML;
      (content || content === '') && dispatch(setMessageContent(content));
    });

    // Наблюдаем за изменениями в дочерних элементах и атрибутах
    observer.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true
    });

    // Очистка при размонтировании
    return () => {
      // const finalContent = element.innerHTML;
      // setMessageContent(finalContent);
      observer.disconnect();
    };
  }, []);

  React.useEffect(() => {
    editableRef.current?.focus();
  }, []);
  
  React.useEffect(() => {
    if (messageForReply && editableRef.current) {
      setCursorToTheEnd(editableRef.current)
    }
  }, [messageForReply]);

  React.useEffect(() => {
    const element = editableRef.current;
    if (!element) return;
    const handleBeforeInput = (event: InputEvent) => {
      const currentText = element.innerText || '';
      // Получаем выделенный текст
      const selection = window.getSelection();
      let selectedText = '';
      let selectionLength = 0;

      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        // Проверяем, что выделение находится внутри нашего элемента
        if (element.contains(range.commonAncestorContainer)) {
          selectedText = range.toString();
          selectionLength = selectedText.length;
        }
      }
      
      let insertedLength = 0;
      
      if (event.data) insertedLength = event.data.length;
      // Если вставляется один символ через IME (composition), пропускаем проверку
      if (event.inputType === 'insertCompositionText' || event.isComposing) {
        return;
      }
      
      // Новая длина текста = текущая длина - длина выделенного текста + длина вставляемого текста
      const newLength = currentText.length - selectionLength + insertedLength;
      
      // Проверяем, не превысит ли новый текст лимит
      if (newLength > MAX_LENGTH) {
        event.preventDefault();
        return false;
      }
    };

    element.addEventListener('beforeinput', handleBeforeInput);
    
    return () => {
      element.removeEventListener('beforeinput', handleBeforeInput);
    };
  }, []);

  const submitOnEnterClick = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const selection = document.getSelection()!;
    const target = selection.focusNode?.parentElement as HTMLElement;
    const nativeEvent = event.nativeEvent;
    const targetElement = selection.focusNode?.parentElement as HTMLElement;

    if (!target.dataset?.current && target.nodeName === 'SPAN' && event.key === 'Backspace') {
      target.remove();
    }
    
    if (event.key === 'Backspace' && editableRef.current?.querySelector('span[data-current="true"]')?.innerHTML === '#') {
      editableRef.current!.innerHTML = '';
      dispatch(setIsOpenDocsMenu(false));
    }

    if (event.key === 'Backspace' && editableRef.current?.querySelector('span[data-current="true"][data-type="user"]')?.innerHTML === '@') {
      editableRef.current!.innerHTML = '';
      dispatch(setIsOpenUsersMenu(false));
    }
    
    if (
      !targetElement.dataset?.current &&
      targetElement.nodeName === 'SPAN' &&
      nativeEvent.key !== 'ArrowRight' &&
      nativeEvent.key !== 'ArrowLeft'
    ) {
      nativeEvent.preventDefault();
      nativeEvent.returnValue = false;
      return false;
    }

    if (event.key === 'Enter' && isSendingDisabled) {
      nativeEvent.preventDefault();
      return false;
    }
    
    if (event.key === 'Enter' && event.ctrlKey === true) {
      event.preventDefault();
      document.execCommand('insertHTML', false, '&nbsp;<br>&nbsp;');
      return false;
    }

    if (event.key === 'Enter' && event.shiftKey === false && !isSendingDisabled) {
      onFinish();
      if (editableRef.current) {
        editableRef.current.innerHTML = '';
      }
      return false;
    }
  };

  const onInput = (event: React.ChangeEvent<HTMLDivElement>) => {
    const nativeEvent = event.nativeEvent as InputEvent;
    const selection = document.getSelection()!;
    const targetElement = selection.focusNode?.parentElement as HTMLElement;
    if (nativeEvent.inputType === 'insertParagraph') event.target.textContent = '';
    const currentValue = editableRef.current!.innerHTML
    const spans = editableRef.current?.querySelectorAll('span');
    const currentSpan = editableRef.current?.querySelector('span[data-current="true"]');
    const searchValue = currentSpan?.textContent?.slice(1);
    const isUserTag = currentSpan?.getAttribute('data-type') === 'user';

    if (searchValue) {
      if (isUserTag) {
        setUserSearch(searchValue);
      } else {
        setSearch(searchValue);
      }
    }

    spans?.forEach((span) => {
      if ((span.childNodes[0] as Text)?.data?.trim().length <= 0 || (span.childNodes[0] as Text).data === ' ') {
        span.remove();
      }
    });
    
    if (nativeEvent.inputType?.startsWith('delete')) {
      const htmlContent = editableRef.current?.innerHTML || '';
      const cleanedContent = htmlContent.replace(/<br\s*\/?>/gi, '').replace(/&nbsp;/gi, '');
      
      if (cleanedContent.length === 0 && editableRef.current) {
        editableRef.current.innerHTML = '';
        return;
      }
    }
        
    if (targetElement.nodeName !== 'SPAN') {
      setSearch('');
      setUserSearch('');
      dispatch(setIsOpenDocsMenu(false));
      dispatch(setIsOpenUsersMenu(false));
    } else if (targetElement.getAttribute('data-type') === 'user') {
      if (!isOpenUsersMenu) dispatch(setIsOpenUsersMenu(true));
    } else if (!isOpenDocsMenu) {
      dispatch(setIsOpenDocsMenu(true));
    }

    if (nativeEvent.data === '/') {
      setIsOpenHelp(true);
    } else if (isOpenHelp) {
      setIsOpenHelp(false);
    }

    const insertTag = (
      tagConstants: typeof CURRENT_INPUT_TAG | typeof CURRENT_USER_INPUT_TAG,
      symbol: string,
      setStateFunction: () => void,
    ) => {
      const before = selection.focusOffset <= 0 ? '' : currentValue.slice(0, currentValue.length - 1);
      editableRef.current!.innerHTML = `${before} ${tagConstants.OPEN}${symbol}${
        tagConstants.CLOSE
      } ${currentValue.slice(currentValue.length)} `;

      document.execCommand('selectAll', false, undefined);
      selection?.collapse(editableRef.current, editableRef.current?.childNodes?.length);
      setStateFunction();
    };

    if (nativeEvent.data === '#' && !aiAssistant) {
      insertTag(CURRENT_INPUT_TAG, '#', () => dispatch(setIsOpenDocsMenu(true)));
    }

    if (nativeEvent.data === '@' && !aiAssistant) {
      insertTag(CURRENT_USER_INPUT_TAG, '@', () => dispatch(setIsOpenUsersMenu(true)));
    }
  };

  // Вспомогательная функция для вставки изображений
  const handleImagePaste = (images: File[]) => {
    onOpenAttachment();
    setAttachment(images);
  };

  const handleTextPaste = (text: string) => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    const currentText = editableRef?.current?.innerText || '';
    const selectedText = range.toString();

    // Расчет доступного места
    const availableSpace = MAX_LENGTH - (currentText.length - selectedText.length);
    if (availableSpace <= 0) return;

    // Обрезка текста при необходимости
    const finalText = text.length > availableSpace 
      ? text.slice(0, availableSpace) 
      : text;

    // Вставка текста
    range.deleteContents();
    const textNode = document.createTextNode(finalText);
    range.insertNode(textNode);

    // Установка курсора после вставленного текста
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);

    // Прокрутка к нижней части
    const element = editableRef?.current;
    element?.scrollTo({ top: element.scrollHeight });
  };

  const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {    
    event.preventDefault();

    const { clipboardData } = event;
    if (!clipboardData) return;

    // Обработка текста
    const text = clipboardData.getData('text/plain');
    if (text) {
      handleTextPaste(text);
      return;
    }

    // Обработка изображений
    const imageFiles = Array.from(clipboardData.files).filter(file => file.type.startsWith('image/'));    
    if (imageFiles[0]) {
      handleImagePaste(imageFiles);
    }
  };

  return (
    <>
      {!aiAssistant && (
        <ConfigProvider theme={{ token: { borderRadius: 0 } }}>
          <Dropdown
            align={{ offset: [0, 0] }}
            menu={{
              items: isLoadingTagsDocuments
                ? [{ key: 'loading', label: <RowLoader width='500px' height='20px' /> }]
                : filteredTags?.length
                  ? filteredTags
                  : [{ key: 'empty', label: t('Documents.table.fallback') }],
            }}
            open={needToShowMenus && isOpenDocsMenu}
            placement='top'
          >
            <div className={classes.tags} />
          </Dropdown>
          <Dropdown
            align={{ offset: [0, 0] }}
            menu={{
              items: filteredUsers?.length
                ? filteredUsers
                : [{ key: 'empty', label: t('Chats.noUsersFound') }],
            }}
            open={needToShowMenus && isOpenUsersMenu}
            placement='top'
          >
            <div className={classes.tags} />
          </Dropdown>
        </ ConfigProvider>
      )}
      {!aiAssistant && (
        <Tooltip
          title={t('Chats.commands.documentsTag')}
          align={{ offset: [0, 0] }}
          open={needToShowMenus && isOpenHelp}
          placement='topLeft'
          styles={{container: {width: 500}}}
          color={colors.primary100}
        >
          <div className={classes.tags} />
        </Tooltip>
      )}

      <div
        autoFocus
        ref={editableRef}
        data-placeholder={aiAssistant ? t('Chats.startTypingAI') : t('Chats.startTyping')}
        onInput={onInput}
        onKeyDown={submitOnEnterClick as any}
        onPaste={onPaste}
        className={cn(classes.textarea, {
          [classes.mText]: parent === 'modal',
          [classes.aiInput]: aiAssistant
        })}
        contentEditable
        // onBlur={() => {
        //   setTimeout(() => {
        //     dispatch(setIsOpenUsersMenu(false));
        //     dispatch(setIsOpenDocsMenu(false));
        //   }, 100);
        // }}
        suppressContentEditableWarning
      />
      {aiAssistant && (
        <ModeDropdown />
      )}
      {parent !== 'modal' && (
        <div
          className={cn(classes.sendingIcons, {
            [classes.aiIcons]: aiAssistant
          })}
        >
          <Button
            type='text'
            disabled={isSendingDisabled}
            onClick={form.submit}
            className={cn(classes.footerIcons, classes.sendIcon, { [classes.sendDisabled]: isSendingDisabled })}
            icon={<SendOutlined />}
          />
        </div>
      )}
    </>
  );
}
